import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import {
  Box,
  Button,
  Typography,
  Divider,
  useTheme,
  styled,
  CircularProgress,
} from '@mui/material';
import { useCallback, useEffect, useState, ComponentProps } from 'react';
import { useLocation } from 'react-router-dom';
import { useSetRecoilState, useRecoilValue, useRecoilState } from 'recoil';

import { deleteProduct, getSingleProduct } from '../../adapter/catalog-service';
import { getOrdersListWithFilter } from '../../adapter/order-service';
import {
  snackbarOpenState,
  snackbarSeverityState,
  snackbarTextState,
} from '../../domain/app';
import { organizationSelector } from '../../domain/organization';
import { Product, ProductInput } from '../../types/catalog';
import { OrderLocalized } from '../../types/order';
import { defaultProductValue } from '../../utils/constants';
import { OrderTableComponent } from '../Orders/OrderTableComponent';
import { ConfirmDialog } from '../Shared/ConfirmDialog';

import { ProductCreationState } from './ProductCreation';
import { ProductDetailComponent } from './ProductDetailComponent';
import { ProductEditStatusState } from './ProductEditStatus';
import { ProductLayout } from './ProductLayout';

// useNavigate を置き換え
import { useNavigateAdminDelegatable as useNavigate } from '@app/administrator/utils/useNavigateAdminDelegatable';
import { orderListStateAtom } from '@app/domain/order';
import {
  useAttributesClinicalDepartment,
  useAttributesJobType,
} from '@app/hooks/catalog';
import { convertOrderLocalized } from '@app/utils/order';
import { unescapeHtml } from '@app/utils/pattern';

const ColBox = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
});

const RowBox = styled(Box)({
  display: 'flex',
  flexDirection: 'row',
});

const StatusBox = styled(Box)({
  borderRadius: 4,
  borderStyle: 'solid',
  borderWidth: 1,
  height: '1.7rem',
  minWidth: '4.2rem',
  paddingInline: 5,
  textAlign: 'center',
});

export interface ProductDetailProps {
  onClose?: () => void;
  previewMode?: boolean;
  productInput?: ProductInput;
}

export interface ProductDetailState {
  id?: string;
}

export function ProductDetail({
  previewMode,
  onClose,
  productInput,
}: ProductDetailProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const organizationState = useRecoilValue(organizationSelector);
  const setSnackbarOpen = useSetRecoilState(snackbarOpenState);
  const setSnackbarText = useSetRecoilState(snackbarTextState);
  const setSnackbarState = useSetRecoilState(snackbarSeverityState);
  const [orders, setOrders] = useRecoilState(orderListStateAtom);
  const theme = useTheme();
  const [shouldShowDeleteDialog, setShouldShowDeleteDialog] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [product, setProduct] = useState<Product | undefined>(undefined);
  const attributeClinicalDepartmentList = useAttributesClinicalDepartment();
  const attributeJobTypeList = useAttributesJobType();

  const productId = (location.state as ProductDetailState | null)?.id ?? '';
  const [isLoadingOrder, setIsLoadingOrder] = useState<boolean>(false);
  const [orderRowCount, setOrderRowCount] = useState(0);
  const [orderPaginationModel, setOrderPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });

  const handleDeleteClick: NonNullable<
    ComponentProps<typeof Button>['onClick']
  > = useCallback(async () => {
    setShouldShowDeleteDialog(() => true);
  }, []);

  const handleCopyClick: NonNullable<ComponentProps<typeof Button>['onClick']> =
    useCallback(async () => {
      navigate('/product/create', {
        state: { id: productId, mode: 'copy' } as ProductCreationState,
      });
    }, [navigate, productId]);

  const handleClickPreview: NonNullable<
    ComponentProps<typeof Button>['onClick']
  > = useCallback(() => {
    // TODO:ドクター画面から見た時のデザインでの確認画面に遷移する
    alert('Coming soon 👷🏼‍♂️');
  }, []);

  const handleClickEdit: NonNullable<ComponentProps<typeof Button>['onClick']> =
    useCallback(() => {
      if (orders.length) {
        navigate('/product/edit/status', {
          state: { id: productId } as ProductEditStatusState,
        });
      } else {
        navigate('/product/create', {
          state: { id: productId, mode: 'edit' } as ProductCreationState,
        });
      }
    }, [navigate, productId, orders]);

  const handleCloseDeleteDialog: ComponentProps<
    typeof ConfirmDialog
  >['onClose'] = useCallback(
    async (confirm) => {
      if (!confirm) {
        setShouldShowDeleteDialog(false);
        return;
      }
      try {
        if (!product) {
          return;
        }
        setIsDeleting(true);
        await deleteProduct(product.id);
        setSnackbarText(`${product.id}のプランを削除しました。`);
        setSnackbarState('info');
        setSnackbarOpen(true);
        navigate('/product');
      } catch (error) {
        setSnackbarText(
          `プランの削除に失敗しました。, ${
            error instanceof Error ? error.message : error
          }`
        );
        setSnackbarOpen(true);
      } finally {
        setIsDeleting(false);
        setShouldShowDeleteDialog(false);
      }
    },
    [product, navigate, setSnackbarOpen, setSnackbarState, setSnackbarText]
  );

  const loadProduct = useCallback(async () => {
    if (productInput) {
      const product: Product = {
        ...defaultProductValue,
        ...productInput,
        geoCoordinatesField: defaultProductValue.geoCoordinatesField,
        id: 'プランID',
        publication: {
          ...defaultProductValue.publication,
          ...productInput.publication,
        },
        variants: [
          {
            ...productInput.variants[0],
            id: '',
            title: '',
          },
        ],
      };
      setProduct(product);
      return product;
    }
    try {
      const result = await getSingleProduct(productId);
      if (result.status !== 200) {
        throw new Error(`${result.status} ${result.statusText}`);
      }
      setProduct(result.data);
      return result.data;
    } catch (error) {
      setSnackbarText(
        `プランの取得に失敗しました, ${
          error instanceof Error ? error.message : error
        }`
      );
      setSnackbarOpen(true);
    }
  }, [productId, productInput, setSnackbarText, setSnackbarOpen]);

  const loadOrders = useCallback(
    async (productId: string | undefined) => {
      if (!productId) {
        return;
      }
      try {
        setIsLoadingOrder(true);
        const result = await getOrdersListWithFilter(organizationState.id, {
          order: 'createdAt',
          pageNumber: orderPaginationModel.page,
          pageSize: orderPaginationModel.pageSize,
          productId,
        });
        if (result.status !== 200) {
          throw new Error(`${result.status} ${result.statusText}`);
        }
        const orders: OrderLocalized[] = result.data.value.map((order) =>
          convertOrderLocalized(order)
        );

        setOrders(orders);
        setOrderRowCount(result.data.total);
      } catch (error: unknown) {
        setSnackbarText(
          `応募の取得に失敗しました, ${
            error instanceof Error ? error.message : error
          }`
        );
        setSnackbarOpen(true);
      } finally {
        setIsLoadingOrder(false);
      }
    },
    [
      orderPaginationModel,
      organizationState,
      setOrders,
      setOrderRowCount,
      setSnackbarOpen,
      setSnackbarText,
    ]
  );

  // データ読み込み済みかどうかの状態
  const [isLoadData, setIsLoadData] = useState(false);

  useEffect(() => {
    void (async () => {
      const product = await loadProduct();
      await loadOrders(product?.id);
      setIsLoadData(true);
    })();
  }, [loadProduct, loadOrders]);

  if (!isLoadData) {
    return (
      <ProductLayout>
        <CircularProgress sx={{ m: 'auto' }} />
      </ProductLayout>
    );
  }

  if (!product) {
    return (
      <ProductLayout>
        <Typography sx={{ m: 'auto' }}>
          ID:{productId}のプランを取得できませんでした
        </Typography>
      </ProductLayout>
    );
  }

  return (
    <>
      <ProductLayout>
        <RowBox gap={1}>
          <ColBox>
            <ColBox color={theme.palette.grey[600]}>
              <Typography>{product.id}</Typography>
            </ColBox>

            <RowBox alignItems="baseline" gap={2}>
              {previewMode ? (
                <StatusBox
                  color={theme.palette.primary.main}
                  borderColor={theme.palette.primary.main}
                >
                  プレビュー
                </StatusBox>
              ) : product.publication?.status === 'ACTIVE' ? (
                <StatusBox
                  color={theme.customPalette.orange}
                  borderColor={theme.customPalette.orange}
                >
                  掲載中
                </StatusBox>
              ) : product.publication?.status === 'ARCHIVED' ? (
                <StatusBox
                  color={theme.customPalette.alert}
                  border={theme.customPalette.alert}
                >
                  終了
                </StatusBox>
              ) : product.publication?.status === 'DRAFT' ? (
                <StatusBox
                  color={theme.palette.grey[500]}
                  border={theme.palette.grey[500]}
                >
                  下書き
                </StatusBox>
              ) : undefined}
              <ColBox>
                <Typography fontSize={'1.8rem'} sx={{ mb: 2, mt: 2 }}>
                  {unescapeHtml(product.name)}
                </Typography>
              </ColBox>
            </RowBox>
          </ColBox>

          <RowBox gap={2} ml="auto" py={1}>
            {previewMode ? (
              <Button
                variant="outlined"
                color="secondary"
                size="small"
                sx={{ width: '8rem' }}
                onClick={onClose}
              >
                <RowBox alignItems="center" gap={1}>
                  <HighlightOffIcon />
                  閉じる
                </RowBox>
              </Button>
            ) : (
              <>
                <Button
                  variant="outlined"
                  size="small"
                  sx={{
                    bgcolor: theme.palette.common.white,
                    border: `solid 1px ${theme.customPalette.alert}`,
                    color: theme.customPalette.alert,
                    width: '5rem',
                  }}
                  onClick={handleDeleteClick}
                  disabled={!!orders.length}
                >
                  削除
                </Button>
                <Button
                  variant="outlined"
                  color="secondary"
                  size="small"
                  sx={{ width: '8rem' }}
                  onClick={handleCopyClick}
                >
                  複製
                </Button>
                <Button
                  variant="outlined"
                  color="secondary"
                  size="small"
                  sx={{
                    display: 'none', // TODO:プレビュー機能が実装されたら削除する
                    width: '8rem',
                  }}
                  onClick={handleClickPreview}
                >
                  プレビュー
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  sx={{ width: '8rem' }}
                  onClick={handleClickEdit}
                  // disabled={!!orders.length}
                >
                  編集
                </Button>
              </>
            )}
          </RowBox>
        </RowBox>

        {!previewMode && orders.length ? (
          <OrderTableComponent
            orders={orders}
            rowCount={orderRowCount}
            paginationModel={orderPaginationModel}
            onPaginationModelChange={setOrderPaginationModel}
            loading={isLoadingOrder}
          />
        ) : (
          <ColBox
            alignItems="center"
            justifyContent="center"
            height="5rem"
            bgcolor={theme.palette.grey[100]}
            border={`solid 2px ${theme.palette.grey[300]}`}
            borderRadius={2}
          >
            <Typography fontWeight={600}>まだ応募がありません</Typography>
          </ColBox>
        )}

        <Divider />

        <ColBox gap={4} pb={4}>
          <Typography variant="h5">募集詳細</Typography>
          <ProductDetailComponent
            product={product}
            attributeClinicalDepartmentList={attributeClinicalDepartmentList}
            attributeJobTypeList={attributeJobTypeList}
          />
        </ColBox>
      </ProductLayout>
      <ConfirmDialog
        title="本当に削除しますか？"
        okButtonText="削除"
        okButtonLoading={isDeleting}
        open={shouldShowDeleteDialog}
        onClose={handleCloseDeleteDialog}
      />
    </>
  );
}
